// TranslateVisaCrossLink — reciprocal cross-link widget
// Helps users move between /translate and /visa hubs (and key sub-pages).
// Use on visa pages with mode="from-visa" and translate pages with mode="from-translate".
import { Link } from "@tanstack/react-router";
import { ArrowRight, FileCheck2, Plane, Stamp, Languages } from "lucide-react";

type Mode = "from-visa" | "from-translate";
type Locale = "th" | "en";

const COPY = {
  "from-visa": {
    th: {
      eyebrow: "บริการเสริมที่ลูกค้าวีซ่าใช้บ่อย",
      heading: "ต้องแปล + รับรองเอกสารก่อนยื่นวีซ่า?",
      sub: "NYC แปล + รับรอง MFA / NAATI / Apostille / Embassy Legalization ครบจบที่เดียว ลูกค้าวีซ่า 80% สั่งแปลพร้อมยื่นวีซ่าในแพ็กเกจเดียว",
      links: [
        { to: "/translate", label: "แปลรายภาษา (24 ภาษา)", desc: "ทะเบียนบ้าน · สูติบัตร · วุฒิ · Bank Statement", icon: Languages },
        { to: "/translate/naati", label: "NAATI สำหรับ AU/NZ", desc: "Skilled / Partner / Student Visa", icon: FileCheck2 },
        { to: "/translate/mfa", label: "MFA Consular Legalization", desc: "รับรองกรมการกงสุล · 3-5 วันทำการ", icon: Stamp },
        { to: "/translate/embassy-legalization", label: "Embassy Legalization", desc: "รับรองสถานทูตปลายทาง (จีน เวียดนาม UAE …)", icon: Stamp },
      ],
    },
    en: {
      eyebrow: "Bundled with your visa application",
      heading: "Need certified translation before your visa?",
      sub: "NYC handles translation + MFA / NAATI / Apostille / Embassy Legalization in one workflow. 80% of visa clients add translation in the same package.",
      links: [
        { to: "/en/translate", label: "Certified Translation (24 langs)", desc: "House reg · Birth cert · Degree · Bank statement", icon: Languages },
        { to: "/en/translate/naati", label: "NAATI for AU/NZ", desc: "Skilled / Partner / Student Visa", icon: FileCheck2 },
        { to: "/en/translate/mfa", label: "MFA Consular Legalization", desc: "Thai MFA stamp · 3-5 business days", icon: Stamp },
        { to: "/en/translate/embassy-legalization", label: "Embassy Legalization", desc: "Destination embassy (CN, VN, UAE …)", icon: Stamp },
      ],
    },
  },
  "from-translate": {
    th: {
      eyebrow: "เอกสารแปลแล้ว · ขั้นต่อไปคือยื่นวีซ่า",
      heading: "ให้ NYC พาไปยื่นวีซ่าต่อเลยไหม?",
      sub: "เรามีบริการวีซ่าครบทุกประเภท Non-B, LTR, DTV, Marriage, Retirement และ Walk-in Service พาไปสถานทูต / ตม.",
      links: [
        { to: "/visa", label: "Visa Hub · ทุกสัญชาติ", desc: "100+ สัญชาติ × 12+ ประเภทวีซ่า", icon: Plane },
        { to: "/checklist", label: "Checklist เอกสาร", desc: "ตรวจสอบเอกสารตามประเภทวีซ่าก่อนเริ่ม", icon: FileCheck2 },
        { to: "/apostille", label: "Apostille / Hague", desc: "รับรอง Apostille ไป 120+ ประเทศ", icon: Stamp },
        { to: "/marriage", label: "จดทะเบียนสมรสต่างชาติ", desc: "เอกสาร + ล่าม + นัดที่ว่าการอำเภอ", icon: FileCheck2 },
      ],
    },
    en: {
      eyebrow: "Translated documents · ready to file your visa",
      heading: "Let NYC file your visa next?",
      sub: "Full visa service: Non-B, LTR, DTV, Marriage, Retirement — plus walk-in support at embassies and Immigration.",
      links: [
        { to: "/en/visa", label: "Visa Hub · all nationalities", desc: "100+ nationalities × 12+ visa types", icon: Plane },
        { to: "/en/checklist", label: "Document checklist", desc: "Required docs per visa type", icon: FileCheck2 },
        { to: "/en/apostille", label: "Apostille / Hague", desc: "Apostille for 120+ countries", icon: Stamp },
        { to: "/en/marriage", label: "Marriage registration", desc: "Translation + interpreter + amphoe booking", icon: FileCheck2 },
      ],
    },
  },
} as const;

export function TranslateVisaCrossLink({
  mode,
  locale = "th",
}: {
  mode: Mode;
  locale?: Locale;
}) {
  const t = COPY[mode][locale];
  return (
    <section className="border-t border-slate-200 bg-gradient-to-br from-slate-50 to-amber-50/40 py-12 sm:py-14">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="mb-7">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
            {t.eyebrow}
          </div>
          <h2 className="mt-1.5 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            {t.heading}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-700 sm:text-[15px]">{t.sub}</p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {t.links.map((l) => {
            const Icon = l.icon;
            return (
              <a
                key={l.to}
                href={l.to}
                className="group flex min-h-[44px] items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-amber-400/60 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-500"
              >
                <div className="rounded-lg bg-amber-100 p-2 text-amber-700">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-[15px] font-extrabold text-navy-deep group-hover:text-amber-700">
                    {l.label}
                  </div>
                  <div className="mt-0.5 text-[12px] text-slate-600">{l.desc}</div>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 text-slate-400 group-hover:text-amber-600" />
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
